import { useState, useEffect, useRef } from 'react';

const WalletFilter = ({ 
  wallets, 
  walletBalances, 
  onFilterChange,
  balancesEnabled,
  isLoading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({
    // Currency amount filters
    minSolAmount: '',
    maxSolAmount: '',
    minTotalValue: '',
    maxTotalValue: '',
    
    // Token filters
    selectedTokens: [], // Which tokens to filter by
    tokenFilterMode: 'any', // 'any', 'all', 'none'
    includeEmptyWallets: true,
    
    // Sort options
    sortBy: 'name', // 'name', 'sol', 'totalValue', 'tokenCount'
    sortOrder: 'asc' // 'asc', 'desc'
  });
  
  const [availableTokens, setAvailableTokens] = useState([]);
  const [tokenDropdownOpen, setTokenDropdownOpen] = useState(false);
  const filterRef = useRef(null);
  const tokenDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      if (tokenDropdownRef.current && !tokenDropdownRef.current.contains(event.target)) {
        setTokenDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Extract unique tokens from wallet balances
  useEffect(() => {
    if (!balancesEnabled || !walletBalances) return;
    
    const tokenMap = new Map();
    
    // Add SOL as a special token option
    let solCount = 0;
    Object.values(walletBalances).forEach(balance => {
      if (balance?.sol && balance.sol > 0) {
        solCount++;
      }
      if (balance?.tokens) {
        balance.tokens.forEach(token => {
          if (!tokenMap.has(token.mint)) {
            tokenMap.set(token.mint, {
              mint: token.mint,
              symbol: getTokenSymbol(token.mint),
              count: 1
            });
          } else {
            const existing = tokenMap.get(token.mint);
            existing.count += 1;
            tokenMap.set(token.mint, existing);
          }
        });
      }
    });
    
    const tokens = Array.from(tokenMap.values()).sort((a, b) => b.count - a.count);
    
    // Add SOL at the beginning if there are wallets with SOL
    if (solCount > 0) {
      tokens.unshift({
        mint: 'SOL',
        symbol: 'SOL',
        count: solCount
      });
    }
    
    setAvailableTokens(tokens);
  }, [walletBalances, balancesEnabled]);

  // Apply filters whenever they change
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, walletBalances]);

  const getTokenSymbol = (mint) => {
    const knownTokens = {
      'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v': 'USDC',
      'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB': 'USDT',
      'So11111111111111111111111111111111111111112': 'WSOL',
      'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263': 'BONK',
      '7vfCXTUXx5WJV5JADk17DUJ4ksgau7utNKj4b963voxs': 'WIF',
      'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN': 'JUP'
    };
    
    return knownTokens[mint] || `${mint.slice(0, 4)}...${mint.slice(-4)}`;
  };

  const applyFilters = () => {
    let filteredWallets = [...wallets];
    
    // Only apply balance filters if balances are enabled
    if (balancesEnabled) {
      // Filter by SOL amount
      if (filters.minSolAmount !== '') {
        const minSol = parseFloat(filters.minSolAmount);
        filteredWallets = filteredWallets.filter(wallet => {
          const balance = walletBalances[wallet._id || wallet.id];
          return balance && balance.sol >= minSol;
        });
      }
      
      if (filters.maxSolAmount !== '') {
        const maxSol = parseFloat(filters.maxSolAmount);
        filteredWallets = filteredWallets.filter(wallet => {
          const balance = walletBalances[wallet._id || wallet.id];
          return balance && balance.sol <= maxSol;
        });
      }
      
      // Filter by tokens
      if (filters.selectedTokens.length > 0) {
        filteredWallets = filteredWallets.filter(wallet => {
          const walletId = wallet._id || wallet.id;
          const balance = walletBalances[walletId];
          
          if (!balance) return false;
          
          const walletTokenMints = balance.tokens ? balance.tokens.map(t => t.mint) : [];
          
          // Add SOL to the list if wallet has SOL balance
          if (balance.sol > 0) {
            walletTokenMints.push('SOL');
          }
          
          switch (filters.tokenFilterMode) {
            case 'any':
              return filters.selectedTokens.some(mint => walletTokenMints.includes(mint));
            case 'all':
              return filters.selectedTokens.every(mint => walletTokenMints.includes(mint));
            case 'none':
              return !filters.selectedTokens.some(mint => walletTokenMints.includes(mint));
            default:
              return true;
          }
        });
      }
      
      // Filter empty wallets
      if (!filters.includeEmptyWallets) {
        filteredWallets = filteredWallets.filter(wallet => {
          const balance = walletBalances[wallet._id || wallet.id];
          return balance && (balance.sol > 0 || (balance.tokens && balance.tokens.length > 0));
        });
      }
    }
    
    // Sort wallets
    filteredWallets.sort((a, b) => {
      let aValue, bValue;
      
      switch (filters.sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'sol':
          aValue = walletBalances[a._id || a.id]?.sol || 0;
          bValue = walletBalances[b._id || b.id]?.sol || 0;
          break;
        case 'tokenCount':
          aValue = walletBalances[a._id || a.id]?.tokens?.length || 0;
          bValue = walletBalances[b._id || b.id]?.tokens?.length || 0;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }
      
      if (filters.sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    onFilterChange(filteredWallets);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleToken = (mint) => {
    setFilters(prev => ({
      ...prev,
      selectedTokens: prev.selectedTokens.includes(mint)
        ? prev.selectedTokens.filter(m => m !== mint)
        : [...prev.selectedTokens, mint]
    }));
  };

  const clearFilters = () => {
    setFilters({
      minSolAmount: '',
      maxSolAmount: '',
      minTotalValue: '',
      maxTotalValue: '',
      selectedTokens: [],
      tokenFilterMode: 'any',
      includeEmptyWallets: true,
      sortBy: 'name',
      sortOrder: 'asc'
    });
  };

  const activeFilterCount = () => {
    let count = 0;
    if (filters.minSolAmount !== '') count++;
    if (filters.maxSolAmount !== '') count++;
    if (filters.selectedTokens.length > 0) count++;
    if (!filters.includeEmptyWallets) count++;
    if (filters.sortBy !== 'name' || filters.sortOrder !== 'asc') count++;
    return count;
  };

  return (
    <div className="relative" ref={filterRef}>
      {/* Filter Button */}
      <button
        onClick={() => !isLoading && setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`
          flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200
          ${isLoading
            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed border border-gray-200 dark:border-gray-600'
            : isOpen 
              ? 'bg-purple-600 text-white shadow-lg' 
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm border border-gray-300 dark:border-gray-600'
          }
        `}
        title={isLoading ? 'Loading wallet balances...' : 'Advanced filters'}
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin" />
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" 
            />
          </svg>
        )}
        <span className="font-medium">
          {isLoading ? 'Loading...' : 'Advanced Filters'}
        </span>
        {!isLoading && activeFilterCount() > 0 && (
          <span className={`
            px-2 py-0.5 text-xs rounded-full font-semibold
            ${isOpen ? 'bg-white text-purple-600' : 'bg-purple-100 text-purple-700'}
          `}>
            {activeFilterCount()}
          </span>
        )}
      </button>

      {/* Filter Dropdown */}
      {isOpen && (
        <div
          className="absolute top-full mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 max-h-[600px] overflow-y-auto transition-colors"
          style={{
            width: window.innerWidth <= 360 ? '300px' :
                   window.innerWidth < 640 ? '320px' :
                   window.innerWidth < 768 ? '480px' :
                   window.innerWidth < 1024 ? '600px' :
                   window.innerWidth < 1280 ? '720px' :
                   window.innerWidth < 1440 ? '800px' : '900px',
            minWidth: window.innerWidth <= 360 ? '300px' : window.innerWidth < 640 ? '320px' : '480px',
            left: window.innerWidth < 640 ? '50%' : 'auto',
            right: window.innerWidth < 640 ? 'auto' :
                   window.innerWidth < 768 ? '0px' :
                   window.innerWidth < 1024 ? '-60px' :
                   window.innerWidth < 1280 ? '-120px' : '0px',
            transform: window.innerWidth < 640 ? 'translateX(-50%)' : 'none'
          }}
        >
          <div className="p-4 sm:p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Filter Wallets</h3>
                <button
                  onClick={clearFilters}
                  className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 font-medium px-2 py-1 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20"
                >
                  Clear All
                </button>
              </div>

              {/* SOL Amount Range */}
              <div className="mb-4 sm:mb-6">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  SOL Balance Range
                </label>
                <div className="flex flex-col xs:flex-row xs:items-center gap-2 xs:gap-3">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minSolAmount}
                    onChange={(e) => updateFilter('minSolAmount', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    step="0.01"
                  />
                  <span className="text-gray-500 dark:text-gray-400 text-center xs:text-left">to</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxSolAmount}
                    onChange={(e) => updateFilter('maxSolAmount', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Token Filters */}
              <div className="mb-4 sm:mb-6">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Filter by Tokens
                </label>
                
                {/* Token selection dropdown */}
                <div className="relative mb-3" ref={tokenDropdownRef}>
                  <button
                    onClick={() => setTokenDropdownOpen(!tokenDropdownOpen)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600 min-h-[40px]"
                  >
                    <span className="text-sm truncate pr-2">
                      {filters.selectedTokens.length === 0
                        ? 'Select tokens...'
                        : `${filters.selectedTokens.length} tokens selected`}
                    </span>
                    <svg className={`w-4 h-4 transition-transform flex-shrink-0 ${tokenDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19 9-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {tokenDropdownOpen && availableTokens.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-32 overflow-y-auto z-20">
                      {availableTokens.map(token => (
                        <label
                          key={token.mint}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer min-h-[36px]"
                        >
                          <input
                            type="checkbox"
                            checked={filters.selectedTokens.includes(token.mint)}
                            onChange={() => toggleToken(token.mint)}
                            className="mr-2 sm:mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded flex-shrink-0"
                          />
                          <span className="text-sm flex-1 text-gray-900 dark:text-gray-100 truncate">{token.symbol}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">{token.count}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Token filter mode */}
                {filters.selectedTokens.length > 0 && (
                  <div className="mt-3">
                    <select
                      value={filters.tokenFilterMode}
                      onChange={(e) => updateFilter('tokenFilterMode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                    >
                      <option value="any">Has ANY of selected tokens</option>
                      <option value="all">Has ALL selected tokens</option>
                      <option value="none">Has NONE of selected tokens</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Empty Wallets Toggle */}
              <div className="mb-4 sm:mb-6">
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="checkbox"
                    checked={filters.includeEmptyWallets}
                    onChange={(e) => updateFilter('includeEmptyWallets', e.target.checked)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded flex-shrink-0"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Include empty wallets</span>
                </label>
              </div>

              {/* Sort Options */}
              <div className="mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Sort By
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select
                    value={filters.sortBy}
                    onChange={(e) => updateFilter('sortBy', e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                  >
                    <option value="name">Name</option>
                    <option value="sol">SOL Balance</option>
                    <option value="tokenCount">Token Count</option>
                  </select>
                  <select
                    value={filters.sortOrder}
                    onChange={(e) => updateFilter('sortOrder', e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                  >
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
      )}
    </div>
  );
};

export default WalletFilter;